import { Role, useGetMyEffectiveRolesQuery } from "auth/api/authApi";
import { getAuthToken, refreshAuthToken } from "auth/authToken";
import { MainContent } from "components/Layout/components/Content";
import { Header, HeaderProps } from "components/Layout/components/Header/Header";
import { MasterLayout } from "components/Layout/components/MasterLayout";
import { SideBar } from "components/Layout/components/SideBar";
import { SupportedApps } from "config/config";
import { useApp } from "hooks/useApp";
import { useEnsureAuthenticated } from "hooks/useEnsureAuthenticated";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

const APP_NAME = "epms-auth-service";

const AuthAppEnvironmentProvider = React.lazy(() => import("authapp/AppEnvironmentProvider"));
const AuthAppContent = React.lazy(() => import("authapp/AuthAppContent"));
const AuthAppMenu = React.lazy(() => import("authapp/AuthAppMenu"));

type AuthAppContainerProps = {
    unauthenticatedHandler: (back: string) => void;
    headerProps: HeaderProps;
};

export const AuthAppContainer = ({ unauthenticatedHandler, headerProps }: AuthAppContainerProps) => {
    const navigate = useNavigate();
    const authToken = getAuthToken();
    const ensureAuthenticated = useEnsureAuthenticated();
    const { data } = useGetMyEffectiveRolesQuery();
    const authApp = useApp(SupportedApps.authApp);

    const hasRole = (role: string) => {
        const roles: Role[] = data?.data ?? [];
        return roles.some((r) => r.name === role && r.app === APP_NAME);
    };

    useEffect(() => {
        refreshAuthToken().catch((e) => {
            // eslint-disable-next-line no-console
            console.error("Auth error", e);
            navigate("/auth");
        });
    }, [navigate]);

    return (
        authApp.isLoaded() && (
            <AuthAppEnvironmentProvider
                authToken={authToken}
                containerVersion={APP_INFO.VERSION}
                configuration={authApp.configuration}
                extraTerminalActions={[]}
                ensureAuthenticated={ensureAuthenticated}
                hasRole={hasRole}
                unauthenticatedHandler={unauthenticatedHandler}
            >
                <MasterLayout>
                    <Header appTitle={headerProps.appTitle} username={headerProps.username} userProfile={headerProps.userProfile} />
                    <SideBar>
                        <AuthAppMenu />
                    </SideBar>
                    <MainContent id="pageContent">
                        <AuthAppContent />
                    </MainContent>
                </MasterLayout>
            </AuthAppEnvironmentProvider>
        )
    );
};
